import { ReactNode, useCallback, useMemo } from "react";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import FriendlyTime from "~/components/shared/FriendlyTime";
import Icon from "~/components/shared/Icon";
import { useNavigate } from "react-router-dom";
import { getDurationString, getDuration } from "~/lib/date";
import BktecBadge from "~/components/shared/BktecBadge";
import { urlForView } from "~/components/build/Show/lib/urlForView";
import { BuildView } from "~/components/build/Show/lib/types/BuildView";
import {
  FilterByOption,
  useFilterStore,
  useStateCounts,
} from "~/components/build/Show/components/Sidebar/useFilterStore";
import Duration from "~/components/shared/Duration";
import { useSteps } from "~/components/build/Show/lib/useSteps";
import { Build } from "~/components/build/Show/lib/types/Build";

// Map build states to display text
const buildStateDisplayMap: { [key: string]: string } = {
  scheduled: "scheduled",
  blocked: "blocked",
  running: "running",
  finished: "finished",
  failed: "failed",
  canceled: "canceled",
  canceling: "canceling",
  not_run: "not run",
  started: "running",
};

// Get a user-friendly cancellation status based on cancel_reason
function getCanceledStatusText(cancelReason: string | null | undefined): string {
  if (!cancelReason) {
    return "canceled";
  }

  switch (cancelReason) {
    case "user_canceled_via_ui":
      return "canceled by user";
    case "user_canceled_via_api":
      return "canceled via API";
    case "build_skipping":
      return "skipped by newer build";
    case "branch_deleted":
      return "canceled (branch deleted)";
    case "organization_locked":
      return "canceled (organization locked)";
    case "by_staff":
      return "canceled by staff";
    case "merge_group_destroyed":
      return "canceled (merge queue)";
    default:
      return "canceled";
  }
}

export default function ExecutionDetailsCard() {
  const navigate = useNavigate();
  const { build } = useBuild();
  const setFilterBy = useFilterStore((state) => state.setFilterBy);

  const handleOpenFailedSteps = useCallback(() => {
    setFilterBy([FilterByOption.Failed]);
    navigate(urlForView(build.path, BuildView.Canvas));
  }, []);

  const handleOpenAllSteps = useCallback(() => {
    setFilterBy([]);
    navigate(urlForView(build.path, BuildView.Canvas));
  }, []);

  const handleOpenAllJobs = useCallback(
    (job_states: FilterByOption[] = []) => {
      setFilterBy(job_states);
      navigate(urlForView(build.path, BuildView.Table));
    },
    [build.path],
  );

  if (Features.ProgressiveBuildShow) {
    return (
      <LazilyLoadedDetailsCard
        build={build}
        handleOpenFailedSteps={handleOpenFailedSteps}
        handleOpenAllSteps={handleOpenAllSteps}
        handleOpenAllJobs={handleOpenAllJobs}
      />
    );
  }
  return (
    <EagerLoadedDetailsCard
      build={build}
      handleOpenFailedSteps={handleOpenFailedSteps}
      handleOpenAllSteps={handleOpenAllSteps}
      handleOpenAllJobs={handleOpenAllJobs}
    />
  );
}

type DetailedCardProps = {
  build: Build;
  handleOpenFailedSteps: () => void;
  handleOpenAllSteps: () => void;
  handleOpenAllJobs: (job_states?: FilterByOption[]) => void;
};

function LazilyLoadedDetailsCard(props: DetailedCardProps) {
  const { build, handleOpenFailedSteps, handleOpenAllSteps, handleOpenAllJobs } = props;

  const agentDisplay = useMemo(() => {
    if (!build.clusterQueues) {
      return { label: null, icon: null };
    }
    if (build.clusterQueues.every((queue) => queue.hosted)) {
      return { label: "Hosted", icon: "heroicons/outline/cloud" };
    }

    if (build.clusterQueues.every((queue) => !queue.hosted)) {
      return { label: "Self-hosted", icon: "heroicons/outline/server-stack" };
    }

    return { label: "Mixed", icon: "custom/outline/14x/agent" };
  }, [build.clusterQueues]);

  const queueDisplay = useMemo(() => {
    if (!build.clusterQueues || build.clusterQueues.length === 0) {
      return {
        label: null,
        path: null,
      };
    }

    if (build.clusterQueues.length === 1) {
      const queue = build.clusterQueues[0];
      return {
        label: queue.key,
        path: queue.path,
      };
    }

    return {
      label: "Multiple queues",
      path: null,
    };
  }, [build.clusterQueues]);

  return (
    <div
      className="shadow-depth-200 flex h-full flex-col rounded-md bg-white p-2 sm:p-4"
      data-testid="build-details-card"
    >
      <div className="mb-2 flex items-center justify-between">
        <h2 className="my-0 text-base font-semibold sm:text-lg">Execution details</h2>
      </div>

      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-3 text-xs sm:text-sm">
        <BuildStatus build={build} />
        <BuildFinishedTime build={build} />
        <ExecutionTime build={build} />
        <QueuedTime build={build} />

        {/* Divider */}
        <div className="w-full border-b" />

        {/* Combined Steps/Failed Steps and Jobs run */}
        <div className="flex w-full flex-wrap gap-x-4 gap-y-3">
          <StepsStatistics
            failedCount={build.statistics?.stepsFailedCount || 0}
            totalSteps={build.statistics?.stepsCount || 0}
            handleOpenFailedSteps={handleOpenFailedSteps}
            handleOpenAllSteps={handleOpenAllSteps}
          />

          <JobsStatistics
            totalStartedJobs={build.statistics?.jobsStartedCount || 0}
            handleOpenAllJobs={() => handleOpenAllJobs(build.availableFilters?.runningJobStates)}
          />
        </div>

        {/* Retries, Agent Type, and Queue */}
        <div className="flex w-full flex-wrap gap-x-4 gap-y-3">
          <RetryCountsStatistics build={build} />
          <AgentsDetails agentTypeDisplay={agentDisplay.label} agentTypeIcon={agentDisplay.icon} />
          <QueuesDetails
            label={queueDisplay.label}
            path={queueDisplay.path}
            queuesCount={build.clusterQueues?.length || 0}
          />
        </div>
      </div>

      {/* BK Tech Badge section */}
      <div className="mt-3 border-t pt-3">
        {build.project.bktecBadgeEnabled && (
          <BktecBadge
            buildId={build.id}
            pipelineHasHistoricalTestPlans={build.project.hasTestPlans}
          />
        )}
      </div>
    </div>
  );
}

function EagerLoadedDetailsCard(props: DetailedCardProps) {
  const { build, handleOpenFailedSteps, handleOpenAllSteps, handleOpenAllJobs } = props;
  const counts = useStateCounts();
  const failedCount = counts["failed"];

  // --- Determine Queue Info ---
  const uniqueQueueKeys = useMemo(() => {
    return build.jobs
      ?.map((job) => job.clusterQueue?.key)
      .filter((key): key is string => !!key) // Filter out null/undefined keys and ensure type is string
      .reduce((acc, key) => {
        if (!acc.includes(key)) {
          acc.push(key);
        }
        return acc;
      }, [] as string[]);
  }, [build.jobs]);

  let queueDisplay: string | null = null;
  if (uniqueQueueKeys && uniqueQueueKeys.length === 1) {
    queueDisplay = uniqueQueueKeys[0];
  } else if (uniqueQueueKeys && uniqueQueueKeys.length > 1) {
    queueDisplay = "Multiple queues";
  }
  // --- End Queue Info ---

  // --- Determine Agent Type Info ---
  const scriptJobs = build.jobs?.filter((job) => job.type === "script");
  const hasHosted = scriptJobs?.some((job) => job.hosted);
  const hasSelfHosted = scriptJobs?.some((job) => !job.hosted);

  let agentTypeDisplay: React.ReactNode = null;
  let agentTypeIcon: string | null = null;

  if (hasHosted && hasSelfHosted) {
    agentTypeDisplay = <div className="text-xs sm:text-sm">Mixed</div>;
    agentTypeIcon = "custom/outline/14x/agent"; // Generic icon for mixed
  } else if (hasHosted) {
    agentTypeDisplay = <div className="text-xs sm:text-sm">Hosted</div>;
    agentTypeIcon = "heroicons/outline/cloud";
  } else if (hasSelfHosted) {
    agentTypeDisplay = <div className="text-xs sm:text-sm">Self-hosted</div>;
    agentTypeIcon = "heroicons/outline/server-stack";
  }
  // --- End Agent Type Info ---

  const { steps } = useSteps();
  const totalSteps = useMemo(() => {
    return steps.filter((step) => step.type !== "group").length;
  }, [steps.length]);

  // Calculate total number of jobs run (script or trigger, not broken)
  const totalStartedJobs = useMemo(() => {
    const startedStates = ["running", "canceling", "finished", "failed", "canceled"];
    return build.jobs?.filter(
      (job) =>
        job.state !== "broken" &&
        (job.type === "script" || job.type === "trigger") &&
        startedStates.includes(job.state),
    ).length;
  }, [build.jobs]);

  return (
    <div
      className="shadow-depth-200 flex h-full flex-col rounded-md bg-white p-2 sm:p-4"
      data-testid="build-details-card"
    >
      <div className="mb-2 flex items-center justify-between">
        <h2 className="my-0 text-base font-semibold sm:text-lg">Execution details</h2>
      </div>

      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-3 text-xs sm:text-sm">
        <BuildStatus build={build} />
        <BuildFinishedTime build={build} />
        <ExecutionTime build={build} />
        <QueuedTime build={build} />

        {/* Divider */}
        <div className="w-full border-b" />

        {/* Combined Steps/Failed Steps and Jobs run */}
        <div className="flex w-full flex-wrap gap-x-4 gap-y-3">
          <StepsStatistics
            failedCount={failedCount}
            totalSteps={totalSteps}
            handleOpenFailedSteps={handleOpenFailedSteps}
            handleOpenAllSteps={handleOpenAllSteps}
          />

          <JobsStatistics
            totalStartedJobs={totalStartedJobs}
            handleOpenAllJobs={handleOpenAllJobs}
          />
        </div>

        {/* Retries, Agent Type, and Queue */}
        <div className="flex w-full flex-wrap gap-x-4 gap-y-3">
          <RetryCountsStatistics build={build} />
          <AgentsDetails agentTypeDisplay={agentTypeDisplay} agentTypeIcon={agentTypeIcon} />
          <QueuesDetails label={queueDisplay} queuesCount={uniqueQueueKeys.length} />
        </div>
      </div>

      {/* BK Tech Badge section */}
      <div className="mt-3 border-t pt-3">
        {build.project.bktecBadgeEnabled && (
          <BktecBadge
            buildId={build.id}
            pipelineHasHistoricalTestPlans={build.project.hasTestPlans}
          />
        )}
      </div>
    </div>
  );
}

function BuildStatus({ build }: { build: Build }) {
  return (
    <div className="min-w-[calc(50%-8px)]">
      <span className="mb-0.5 block text-gray-900">Status</span>
      <div className="flex items-center gap-1">
        <div className="text-xs capitalize sm:text-sm">
          {buildStateDisplayMap[build.state] || build.state}
        </div>
      </div>
    </div>
  );
}

function BuildFinishedTime({ build }: { build: Build }) {
  if (!build.finishedAt) {
    return null;
  }

  return (
    <div className="min-w-[calc(50%-8px)]">
      <span className="mb-0.5 block text-gray-900">
        {build.state === "canceled" || build.state === "canceling" ? "Cancelled" : "Finished"}
      </span>
      <div className="flex items-center gap-1">
        <div className="text-xs sm:text-sm">
          <FriendlyTime value={build.finishedAt} capitalized={true} />
        </div>
      </div>
    </div>
  );
}

function QueuedTime({ build }: { build: Build }) {
  let queueDurationStr: string | null = null;

  if (build.scheduledAt && build.startedAt) {
    const durationSeconds = getDuration(build.scheduledAt, build.startedAt).asSeconds();
    if (durationSeconds >= 0) {
      queueDurationStr = getDurationString(durationSeconds, "short");
    }
  }

  if (!queueDurationStr) {
    return null;
  }

  return (
    <div className="min-w-[calc(50%-8px)]">
      <span className="mb-0.5 block text-gray-900">Queued time</span>
      <div className="text-xs sm:text-sm">{queueDurationStr}</div>
    </div>
  );
}

function ExecutionTime({ build }: { build: Build }) {
  if (["canceled", "canceling", "not_run"].includes(build.state)) {
    return null;
  }

  return (
    <div className="min-w-[calc(50%-8px)]">
      <span className="mb-0.5 block text-gray-900">Execution time</span>
      <div className="flex items-center gap-1 text-xs sm:text-sm">
        {/* Show Waiting if scheduled/blocked and not started, otherwise show duration/running */}
        {build.startedAt ? (
          // Only pass 'to' if build is finished, otherwise omit the prop
          build.finishedAt ? (
            <Duration.Short from={build.startedAt} to={build.finishedAt} updateFrequency={1000} />
          ) : (
            <Duration.Short from={build.startedAt} updateFrequency={1000} />
          )
        ) : build.state === "scheduled" || build.state === "blocked" ? (
          // Build has not started and is scheduled/blocked
          build.scheduledAt ? (
            <>
              Waiting <Duration.Short from={build.scheduledAt} updateFrequency={1000} />
            </>
          ) : (
            <>Waiting</> // Fallback in case scheduledAt is not available
          )
        ) : null}
      </div>
    </div>
  );
}

type StepsStatistics = {
  failedCount: number;
  totalSteps: number;
  handleOpenFailedSteps: () => void;
  handleOpenAllSteps: () => void;
};

function StepsStatistics(props: StepsStatistics) {
  return (
    <div className="min-w-[calc(50%-8px)]">
      <span className="mb-0.5 block text-gray-900">Steps</span>
      <div className="flex items-center gap-1 text-xs sm:text-sm">
        <button
          onClick={props.handleOpenAllSteps}
          className="text-charcoal-700 hover:text-charcoal-900 inline-flex border-none bg-transparent p-0 text-xs font-medium hover:underline sm:text-sm"
        >
          {props.totalSteps ?? "N/A"}
        </button>

        {props.failedCount > 0 && (
          <div className="group flex items-center gap-0.5 rounded-md bg-red-100 px-2">
            <button
              onClick={props.handleOpenFailedSteps}
              className="flex inline-flex items-center gap-0 border-none bg-transparent p-0 text-xs font-medium text-red-600 group-hover:text-red-800 group-hover:underline sm:text-sm"
            >
              {props.failedCount} Failed
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

type JobsStatistics = {
  totalStartedJobs: number | undefined;
  handleOpenAllJobs: () => void;
};

function JobsStatistics(props: JobsStatistics) {
  return (
    <div className="min-w-[calc(50%-8px)]">
      <span className="mb-0.5 block text-gray-900">Jobs run</span>

      <button
        onClick={() => props.handleOpenAllJobs()}
        className="text-charcoal-700 hover:text-charcoal-900 inline-flex border-none bg-transparent p-0 text-xs font-medium hover:underline sm:text-sm"
      >
        {props.totalStartedJobs ?? "N/A"}
      </button>
    </div>
  );
}

type RetryCountsStatisticsProps = {
  build: Build;
};

function RetryCountsStatistics(props: RetryCountsStatisticsProps) {
  const { build } = props;

  if (!build.retry_counts || build.retry_counts.total === 0) {
    return null;
  }

  return (
    <div className="min-w-[calc(50%-8px)]">
      <span className="mb-0.5 block text-gray-900">Retries</span>
      <div className="text-xs sm:text-sm">
        {build.retry_counts.total}
        <span className="ml-1 text-xs font-normal text-gray-500">
          ({build.retry_counts.manual} Manual, {build.retry_counts.automatic} Automatic)
        </span>
      </div>
    </div>
  );
}

type AgentsDetailsProps = {
  agentTypeDisplay: React.ReactNode;
  agentTypeIcon: string | null;
};

function AgentsDetails(props: AgentsDetailsProps) {
  const { agentTypeDisplay, agentTypeIcon } = props;

  if (!agentTypeDisplay || !agentTypeIcon) {
    return null;
  }

  return (
    <div className="min-w-[calc(50%-8px)]">
      <span className="mb-0.5 block text-gray-900">Agent type</span>
      <div className="flex items-center gap-1 text-xs sm:text-sm">
        <Icon icon={agentTypeIcon} className="!m-0 h-4 w-4 text-gray-900" />
        {agentTypeDisplay}
      </div>
    </div>
  );
}

type QueuesDetailsProps = {
  label: string | null;
  path?: string | null;
  queuesCount: number;
};

function QueuesDetails(props: QueuesDetailsProps) {
  if (!props.label || !props.queuesCount || props.queuesCount === 0) {
    return null;
  }

  return (
    <div className="min-w-[calc(50%-8px)]">
      <span className="mb-0.5 block text-gray-900">
        {props.queuesCount === 1 ? "Queue" : "Queues"}
      </span>
      <div className="flex items-center gap-1 text-xs sm:text-sm">
        <Icon icon="custom/outline/14x/queue" className="!m-0 h-4 w-4 text-gray-900" />
        {props.path ? (
          <a
            href={props.path}
            target="_blank"
            rel="noopener noreferrer"
            className="text-charcoal-700 hover:text-charcoal-900 focus:text-charcoal-900 truncate text-xs hover:underline sm:text-sm"
          >
            {props.label}
          </a>
        ) : (
          <span className="text-xs sm:text-sm">{props.label}</span>
        )}
      </div>
    </div>
  );
}
